import { useEffect, useRef, useState } from 'react';
import { fingeringApi } from '../services/fingeringApi';
import type { JobStatusValue } from '../services/fingeringApi';

/** ポーリングのデフォルト間隔 (ミリ秒)。 */
export const DEFAULT_POLL_INTERVAL_MS = 2000;

/** ネットワーク一瞬断を吸収する retry 上限。 これを超えたら onError を呼ぶ。 */
export const MAX_RETRY_COUNT = 5;
/**
 * 404→expired を即確定にしない猶予回数 (issue #36 F-2)。
 * reload 直後 / server cold start の transient 404 で entry を永久削除しないため、
 * expired を連続で N 回見て初めて確定する。それまでは backoff で再試行する。
 * ネットワーク例外 retry (MAX_RETRY_COUNT) とは別カウンタ。
 */
export const MAX_EXPIRED_RETRY_COUNT = 5;
/** retry 時の backoff 倍率。 intervalMs * BACKOFF_MULTIPLIER だけ待ってから再試行。 */
const BACKOFF_MULTIPLIER = 2;

/** done で停止扱いとなる状態。 */
const SUCCESS_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>(['done']);
/** error 扱いで停止する状態。
 *  'error_processing' は score-engine が失敗時に返す status (issue #41)。 'failed' と同等にマップ。 */
const FAILURE_STATUSES: ReadonlySet<JobStatusValue> = new Set<JobStatusValue>([
  'failed',
  'expired',
  'error_processing',
]);

export interface UseJobStatusPollingOptions {
  /** ポーリング間隔 (ミリ秒)。既定 2000。 */
  intervalMs?: number;
  /** status が done になったとき jobId 付きで 1 回だけ呼ばれる。 */
  onDone?: (jobId: string) => void;
  /**
   * status が failed/expired、もしくは取得失敗のとき 1 回だけ呼ばれる。
   * kind で失敗種別を区別する (復元失敗の文言出し分け用、issue #36 M-2 + #72)。
   * - 'expired': 404→expired が budget 超過で確定 (有効期限切れ)。
   * - 'failed': server が failed/error_processing を返した、 もしくは status 取得が retry budget 超過。
   * - 'watchdog': frontend watchdog (#72) が deadlineAt 超過 + 最終 polling で救済不能 → 確定。
   */
  onError?: (message: string, kind: 'expired' | 'failed' | 'watchdog') => void;
  /** retry budget 上限。 既定 MAX_RETRY_COUNT (5)。 テスト用に 0 を渡せば retry 無効。 */
  maxRetryCount?: number;
  /**
   * 404→expired を確定にするまでの猶予回数 (issue #36 F-2)。 既定 MAX_EXPIRED_RETRY_COUNT (5)。
   * 0 を渡すと従来通り 1 回の expired で即確定する。
   */
  maxExpiredRetryCount?: number;
  /**
   * watchdog (issue #36 パターンA): 投入からの絶対上限時刻 (epoch ms)。
   * processing/queued のまま Date.now() がこれを超えたら、タイムアウト扱いで
   * onError(message, 'failed') を呼ぶ (サーバ #68 の多重防御)。 未指定なら watchdog 無効。
   * done/failed/expired の終端遷移はタイムアウトより優先する (超過後でも done なら拾う)。
   */
  deadlineAt?: number;
}

export interface UseJobStatusPollingResult {
  status: JobStatusValue | null;
  queuePosition?: number;
  error: string | null;
}

/**
 * jobId を受け取り GET /jobs/<id> を定期ポーリングして進行状態を返す hook。
 * done / failed / expired で自動停止し、onDone / onError を 1 回だけ通知する。
 * 結果本体 (GET /jobs/<id>/result) の取得は呼び出し側の責務 (job_type 非依存に保つ)。
 */
export function useJobStatusPolling(
  jobId: string | null,
  options: UseJobStatusPollingOptions = {},
): UseJobStatusPollingResult {
  const {
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    onDone,
    onError,
    maxRetryCount = MAX_RETRY_COUNT,
    maxExpiredRetryCount = MAX_EXPIRED_RETRY_COUNT,
    deadlineAt,
  } = options;

  const [status, setStatus] = useState<JobStatusValue | null>(null);
  const [queuePosition, setQueuePosition] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  // コールバックは ref 経由で参照し、再購読を起こさない。
  const onDoneRef = useRef(onDone);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onDoneRef.current = onDone;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    if (!jobId) {
      setStatus(null);
      setQueuePosition(undefined);
      setError(null);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;
    let retryCount = 0;
    // 連続 expired 回数。 processing 等の非 expired を見たらリセットする (issue #36 F-2)。
    let expiredCount = 0;

    const finish = (next: () => void) => {
      if (settled) return;
      settled = true;
      next();
    };

    const poll = async () => {
      try {
        const res = await fingeringApi.getJobStatus(jobId);
        if (cancelled) return;
        // 成功したので retry count をリセット (連続失敗のみを retry 上限の対象に)
        retryCount = 0;
        setStatus(res.status);
        setQueuePosition(res.queuePosition);

        if (SUCCESS_STATUSES.has(res.status)) {
          finish(() => onDoneRef.current?.(jobId));
          return;
        }
        if (res.status === 'expired') {
          // 404→expired は server cold start / reload 直後の transient な可能性がある。
          // budget 未満の連続 expired は確定せず backoff で再試行し、processing 等が来れば復帰を狙う (issue #36 F-2)。
          expiredCount += 1;
          if (expiredCount < maxExpiredRetryCount) {
            timer = setTimeout(poll, intervalMs * BACKOFF_MULTIPLIER);
            return;
          }
          const message = res.error ?? 'ジョブの有効期限が切れました';
          setError(message);
          finish(() => onErrorRef.current?.(message, 'expired'));
          return;
        }
        // expired 以外の進行状態を見たら expired 連続カウンタをリセット。
        expiredCount = 0;
        if (FAILURE_STATUSES.has(res.status)) {
          const message = res.error ?? 'ジョブの処理に失敗しました';
          setError(message);
          finish(() => onErrorRef.current?.(message, 'failed'));
          return;
        }
        // watchdog (issue #36 パターンA + #72): processing/queued のまま絶対上限時刻を超えた場合、
        // 即 onError ではなく最終 polling を 1 回追加し race を救済する (#72 §2.2.1)。
        // - 最終 polling 中に done/failed が確定していれば優先する
        // - 取得失敗 (例外) は silent catch して watchdog 確定に倒す
        // - cancelled ガード必須 (await 中の unmount 漏れ防止)
        if (deadlineAt !== undefined && Date.now() >= deadlineAt) {
          let finalRes: Awaited<ReturnType<typeof fingeringApi.getJobStatus>> | null = null;
          try {
            finalRes = await fingeringApi.getJobStatus(jobId);
          } catch {
            // race 救済不可 → watchdog 確定に倒す
          }
          if (cancelled) return;
          if (finalRes?.status === 'done') {
            finish(() => onDoneRef.current?.(jobId));
            return;
          }
          if (finalRes && (finalRes.status === 'failed' || finalRes.status === 'error_processing')) {
            const message = finalRes.error ?? 'ジョブの処理に失敗しました';
            setError(message);
            finish(() => onErrorRef.current?.(message, 'failed'));
            return;
          }
          // 依然 processing/queued or 取得不能 → watchdog 確定
          const message = '処理が制限時間 (30 分) を超えました。 もう一度お試しください';
          setError(message);
          finish(() => onErrorRef.current?.(message, 'watchdog'));
          return;
        }
        timer = setTimeout(poll, intervalMs);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'ジョブ状態の取得に失敗しました';

        // retry budget 内なら backoff で再試行
        if (retryCount < maxRetryCount) {
          retryCount += 1;
          timer = setTimeout(poll, intervalMs * BACKOFF_MULTIPLIER);
          return;
        }

        // budget 超過: 通信断は expired ではないので kind='failed' で確定 (issue #36 M-2)。
        setError(message);
        finish(() => onErrorRef.current?.(message, 'failed'));
      }
    };

    setStatus(null);
    setQueuePosition(undefined);
    setError(null);
    poll();

    // #75 系統 2: タブ復帰 (visible) で残り interval を待たず即 re-poll する。
    // hidden 中に backoff timer が止まったり deferred されたりして polling が固まった状態を解消する。
    // settled 後 / cancelled 後は no-op (再 poll しない)。
    const handleVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      if (settled || cancelled) return;
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
      void poll();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [jobId, intervalMs, maxRetryCount, maxExpiredRetryCount, deadlineAt]);

  return { status, queuePosition, error };
}
